import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Newsletter signup — honestly captures interest through the existing
 * /contact endpoint. Does not fake a subscribed state we cannot yet honor;
 * simply confirms we received the address and will email when the list opens.
 */
export default function NewsletterSignup({ variant = "light" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const dark = variant === "dark";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        full_name: "Newsletter interest",
        email,
        subject: "Newsletter signup",
        message:
          "This collector asked to be notified when the newsletter launches.",
        consent: {
          required_agreement: true,
          marketing_email: true,
          marketing_sms: false,
        },
      });
      setDone(true);
      setEmail("");
      toast.success("You're on the list — we'll email when it launches.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className={`rounded-2xl border-2 ${dark ? "border-white/30 bg-white/5 text-white" : "border-[var(--ink)] bg-[var(--panini-yellow)] text-[var(--ink)]"} p-5`}
      >
        <div className="font-display text-lg">You&apos;re in.</div>
        <p className={`text-sm mt-1 ${dark ? "text-white/70" : "text-[var(--ink)]/75"}`}>
          We&apos;ll email you when the newsletter goes live.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className={`flex flex-col sm:flex-row gap-3 ${dark ? "" : ""}`}
    >
      <div
        className={`flex-1 flex items-center rounded-2xl border-2 px-4 ${
          dark
            ? "border-white/30 bg-white/[0.06] focus-within:border-[var(--panini-yellow)]"
            : "border-[var(--ink)] bg-white"
        }`}
      >
        <Mail size={16} className={dark ? "text-white/60 mr-3" : "text-[var(--ink)] mr-3"} />
        <input
          type="email"
          required
          data-testid="newsletter-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@collector.com"
          className={`flex-1 bg-transparent py-3 outline-none text-sm ${
            dark ? "text-white placeholder:text-white/40" : "text-[var(--ink)] placeholder:text-[var(--smoke)]"
          }`}
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="newsletter-submit"
        className={`${dark ? "btn-yellow" : "btn-red"} justify-center disabled:opacity-60`}
      >
        {busy ? "…" : "Join"} <ArrowRight size={16} />
      </button>
    </form>
  );
}
